import { useState, useEffect, useCallback, ChangeEvent } from 'react';
import { useCooperativeId } from 'hooks';
import { useFormik } from 'formik';
import {
  InitTodoForm,
  TaskTodoCreateOptions,
  Option,
  EntityRef,
  TaskTodo,
} from 'models';
import _set from 'lodash/set';
import _isEqual from 'lodash/isEqual';
import { customFormat, toDate } from 'utils';
import { Warning, convertToServerFormat } from './helpers';

import { MAX_TODO_DESCRIPTION_LENGTH } from 'shared/constants';

import services from 'services';

import { TodoCreateFormDetails, Modal } from 'components';

interface Props {
  handleClose: (upload?: boolean) => void;
  initForm: InitTodoForm | null;
  handleSetInitTodoForm: (form: InitTodoForm) => void;
}

interface LocaleState {
  options: TaskTodoCreateOptions | null;
  loading: boolean;
  error: boolean;
  confirmationWarning: boolean;
  upload: boolean;
  saveAndCopyUpload: boolean;
  files: File[];
}

const TodoCreateForm: React.FC<Props> = ({
  handleClose,
  initForm,
  handleSetInitTodoForm,
}) => {
  const coopId = useCooperativeId();
  const [state, setState] = useState<LocaleState>({
    options: null,
    loading: false,
    error: false,
    confirmationWarning: false,
    upload: false,
    files: [],
    saveAndCopyUpload: false,
  });
  const formik = useFormik({
    validateOnMount: false,
    validateOnBlur: false,
    validateOnChange: false,
    initialValues: (initForm
      ? { ...initForm, Executor: null }
      : {
          Subject: null,
          Description: null,
          ResolutionDescription: null,
          StartDate: null,
          DueDate: null,
          Status: null,
          Recurring: { Label: 'Kuukausittain', Value: 752560000 },
          Executor: null,
          ShowOnEventOfBoardMeeting: false,
          ShowOnEventOfGeneralMeeting: false,
          Priority: { Label: 'Normaali', Value: 1 },
        }) as InitTodoForm,
    validate: (values) => {
      let errors = {};

      if (!values.Subject) {
        _set(errors, 'Subject', Warning.Required);
        return errors;
      }

      if (values.Subject.length > 200) {
        _set(errors, 'Subject', Warning.SymbolsLimit);
        return errors;
      }

      if (
        values.Description &&
        values.Description.length > MAX_TODO_DESCRIPTION_LENGTH
      ) {
        _set(errors, 'Description', Warning.DescriptionSymbolsLimit);
      }

      if (
        values.ResolutionDescription &&
        values.ResolutionDescription.length > MAX_TODO_DESCRIPTION_LENGTH
      ) {
        _set(errors, 'ResolutionDescription', Warning.DescriptionSymbolsLimit);
      }

      if (!values.DueDate) {
        _set(errors, 'DueDate', Warning.Required);
        return errors;
      }

      if (!values.Executor) {
        _set(errors, 'Executor', Warning.Required);
        return errors;
      }

      return errors;
    },
    onSubmit: async (values) => {
      const body = {
        Description: values.Description,
        DueDate: customFormat(toDate(values.DueDate), 'server') || '',
        ExecutorId: values.Executor?.Id || '',
        Priority: convertToServerFormat(values.Priority?.Value),
        Recurring: values.Recurring?.Value || '',
        ResolutionDescription: values.ResolutionDescription || '',
        ShowOnEventOfBoardMeeting: values.ShowOnEventOfBoardMeeting,
        ShowOnEventOfGeneralMeeting: values.ShowOnEventOfGeneralMeeting,
        StartDate: customFormat(toDate(values.StartDate), 'server') || '',
        Status: convertToServerFormat(values.Status?.Value),
        Subject: values.Subject,
      };

      try {
        const res: TaskTodo = (
          await services.post(`/api/cooperatives/${coopId}/tasks/todo`, body)
        ).data.Data;

        if (state.files.length && res.Id) {
          await uploadFiles(res.Id);
        }

        return res;
      } catch (err) {
        console.error(err);
        setState((prevState) => ({ ...prevState, upload: false }));
      }
    },
  });

  const uploadFiles = async (taskId: string) => {
    const formData = new FormData();

    for (const file of state.files) {
      formData.append('file', file, file.name);
    }

    await services.post(
      `/api/user/cooperatives/${coopId}/documents/task/${taskId}`,
      formData
    );
  };

  useEffect(() => {
    if (coopId) {
      (async function () {
        setState((prevState) => ({
          ...prevState,
          loading: true,
          error: false,
        }));

        try {
          const options = await fetchOptions(coopId);

          setState((prevState) => ({
            ...prevState,
            loading: false,
            options: {
              ...options,
              Executor: options.Executor.map((executor) => {
                const name = `${executor.Name} ${
                  executor.Roles.length ? `(${executor.Roles.join(', ')})` : ''
                }`;

                return {
                  ...executor,
                  Name: name,
                };
              }),
              Status: [
                {
                  Label: 'Työn alla',
                  Value: 4,
                },
              ],
            },
          }));
        } catch (err) {
          console.error(err);
          setState((prevState) => ({
            ...prevState,
            loading: false,
            error: true,
          }));
        }
      })();
    }
  }, [coopId]);

  const fetchOptions = async (coopId: string) => {
    const options: TaskTodoCreateOptions = (
      await services.get(
        `/api/cooperatives/${coopId}/tasks/todo/create/options`
      )
    ).data.Data;

    return options;
  };

  const handleChangeFiles = useCallback((newFiles: File[]) => {
    setState((prevState) => ({
      ...prevState,
      files: newFiles,
    }));
  }, []);

  const handleChangeInput = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    formik.setFieldValue(name, value);

    /* eslint-disable-next-line */
  }, []);

  const handleChangeSelectValue = useCallback(
    (value: Option | EntityRef, name?: string) => {
      if (name) {
        formik.setFieldValue(name, value);
      }

      /* eslint-disable-next-line */
    },
    []
  );

  const handleChangeTextArea = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const { name, value } = e.target;

      formik.setFieldValue(name, value);

      /* eslint-disable-next-line */
    },
    []
  );

  const handleSaveAndCopy = useCallback(async () => {
    await formik.validateForm();

    if (formik.isValid) {
      setState((prevState) => ({ ...prevState, saveAndCopyUpload: true }));
      const res = await formik.submitForm();

      if (res) {
        handleSetInitTodoForm(formik.values);
      } else {
        setState((prevState) => ({ ...prevState, saveAndCopyUpload: false }));
      }
    }
  }, [formik, handleSetInitTodoForm]);

  const closeWithSaving = useCallback(async () => {
    await formik.validateForm();

    if (formik.isValid) {
      setState((prevState) => ({
        ...prevState,
        upload: true,
        confirmationWarning: false,
      }));

      const res = await formik.submitForm();

      if (res) {
        handleClose(true);
      } else {
        setState((prevState) => ({ ...prevState, upload: false }));
      }
    }
  }, [formik]);

  const handleChangeDate = useCallback((date: Date | null, name?: string) => {
    if (name) {
      let transformedDate = date ? date.toString() : null;

      formik.setFieldValue(name, transformedDate);
    }

    /* eslint-disable-next-line */
  }, []);

  const handleChangeDescription = useCallback(
    (value) => {
      formik.setFieldValue('Description', value);
    },
    [formik]
  );

  const handleChangeResolutionDescription = useCallback(
    (value) => {
      formik.setFieldValue('ResolutionDescription', value);
    },
    [formik]
  );

  const handleInitErrors = useCallback(() => {
    formik.setErrors({});

    /* eslint-disable-next-line */
  }, []);

  const handleCloseTask = useCallback(() => {
    const equal = _isEqual(initForm, formik.values);
    if (equal) {
      handleClose();
      return;
    }

    setState((prevState) => ({ ...prevState, confirmationWarning: true }));
    /* eslint-disable-next-line */
  }, [formik.values]);

  const closeWithoutSaving = () => {
    setState((prevState) => ({ ...prevState, confirmationWarning: false }));
    handleClose();
  };

  return (
    <Modal open maxWidth="lg" fullWidth handleClose={handleCloseTask}>
      <TodoCreateFormDetails
        {...state}
        handleSaveAndCopy={handleSaveAndCopy}
        handleChangeDate={handleChangeDate}
        handleChangeFiles={handleChangeFiles}
        handleChangeInput={handleChangeInput}
        handleChangeSelect={handleChangeSelectValue}
        handleChangeTextArea={handleChangeTextArea}
        handleInitErrors={handleInitErrors}
        handleCloseTask={handleCloseTask}
        closeWithSaving={closeWithSaving}
        closeWithoutSaving={closeWithoutSaving}
        validationErrors={formik.errors}
        values={formik.values}
        onChangeDescription={handleChangeDescription}
        onChangeResolutionDescription={handleChangeResolutionDescription}
      />
    </Modal>
  );
};

export default TodoCreateForm;
